import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Loader2, CheckCircle2, AlertTriangle, Lightbulb } from "lucide-react";
import { supabase } from "@/lib/supabase";

// =============================================================================
// FeedbackModal — T-468. "Report a problem" / "Suggest an idea", filed from the
// screen you're standing on.
//
// Open to EVERY signed-in role (field included) — a crew member hitting a
// broken screen is exactly who we most need to hear from, and they don't get
// the assistant. The current route + browser are captured automatically so a
// report is actionable without the person having to describe where they were.
// =============================================================================

const APP_VERSION = "web 0.1.0";

export default function FeedbackModal({
  open, onClose, defaultKind = "issue",
}: { open: boolean; onClose: () => void; defaultKind?: "issue" | "idea" }) {
  const location = useLocation();
  const [kind, setKind]         = useState<"issue" | "idea">(defaultKind);
  const [title, setTitle]       = useState("");
  const [body, setBody]         = useState("");
  const [severity, setSeverity] = useState("medium");
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setTitle(""); setBody(""); setSeverity("medium"); setDone(false); setErr(null); setKind(defaultKind);
  }
  function close() { reset(); onClose(); }

  async function submit() {
    if (!title.trim()) { setErr("Give it a short title so it's findable."); return; }
    setBusy(true); setErr(null);
    try {
      // created_by is filled by the column default (app.db_user_id()), which is
      // also what the RLS with-check compares against — don't send it.
      const { error } = await supabase.from("feedback").insert({
        kind,
        title: title.trim(),
        body: body.trim() || null,
        context_route: location.pathname,
        severity: kind === "issue" ? severity : null,
        source: "manual",
        user_agent: navigator.userAgent.slice(0, 400),
        app_version: APP_VERSION,
      });
      if (error) throw error;
      setDone(true);
    } catch (e: any) {
      setErr(e?.message ?? "Could not send that. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={close}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[16px] font-semibold flex items-center gap-2">
              <MessageSquarePlus size={17} className="text-accent" /> Tell us what's up
            </h3>
            <p className="text-[12px] text-text-muted mt-1">
              Something broken, or an idea worth building. We can't fix what we don't know about.
            </p>
          </div>
          <button onClick={close} className="text-text-muted hover:text-text p-2.5 -m-1.5 rounded-md" type="button"><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={34} className="text-ok mx-auto mb-3" />
            <div className="text-[15px] font-medium text-text">Thanks — that's logged.</div>
            <p className="text-[12px] text-text-muted mt-1.5">
              It's gone to the directors with the screen you were on attached.
            </p>
            <button onClick={close} className="mt-5 text-[13px] text-accent hover:underline" type="button">Close</button>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setKind("issue")}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded border text-[13px] ${kind === "issue" ? "border-accent bg-accent/10 text-text" : "border-line text-text-muted hover:bg-ink-hover"}`}>
                  <AlertTriangle size={14} /> Something's broken
                </button>
                <button type="button" onClick={() => setKind("idea")}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded border text-[13px] ${kind === "idea" ? "border-accent bg-accent/10 text-text" : "border-line text-text-muted hover:bg-ink-hover"}`}>
                  <Lightbulb size={14} /> I've got an idea
                </button>
              </div>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">
                  {kind === "issue" ? "What went wrong?" : "What would you like?"}
                </span>
                <input value={title} onChange={e => setTitle(e.target.value)} maxLength={140}
                  placeholder={kind === "issue" ? "e.g. Can't save a timesheet on my phone" : "e.g. Let me duplicate last week's roster"}
                  className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink text-text text-[13px] placeholder:text-text-dim focus:outline-none focus:border-accent" />
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">More detail (optional)</span>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
                  placeholder={kind === "issue" ? "What were you doing when it happened? What did you expect?" : "What problem would it solve for you?"}
                  className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink text-text text-[13px] placeholder:text-text-dim focus:outline-none focus:border-accent resize-none" />
              </label>

              {kind === "issue" && (
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-text-muted">How bad is it?</span>
                  <select value={severity} onChange={e => setSeverity(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink text-text text-[13px]">
                    <option value="low">Annoying, but I can work around it</option>
                    <option value="medium">Slowing me down</option>
                    <option value="high">Stopping me working</option>
                  </select>
                </label>
              )}

              <p className="text-[11px] text-text-dim">
                We'll automatically attach the screen you're on ({location.pathname}) and your browser details.
              </p>
              {err && <div className="text-[12px] text-danger">{err}</div>}
            </div>

            <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
              <button onClick={close} type="button" className="px-3 py-2 text-[13px] text-text-muted hover:text-text">Cancel</button>
              <button onClick={submit} disabled={busy} type="button"
                className="px-4 py-2 rounded bg-accent text-white text-[13px] font-medium disabled:opacity-50 inline-flex items-center gap-2">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send it"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
